/**
 * Code 128 of a job number, as module widths. Appendix L, `JOB-011`, `OPS-120`.
 *
 * Appendix L: "Job envelope carries Code 128 of the job number plus the number in human-readable
 * form beneath", and the job ticket row requires the same. Nothing in the repository encoded one:
 * grepping for `code128` found no implementation and no dependency.
 *
 * **Written here rather than added as a dependency**, because the whole of Code 128 Set B is one
 * lookup table and a mod-103 checksum, and a barcode library brings a renderer, a font loader and a
 * canvas abstraction for a sixty-line problem. `OPS-120` needs the same encoder for internal stock
 * barcodes, so it belongs somewhere both can reach rather than inside one screen.
 *
 * **The table was not typed from memory.** 107 patterns typed by hand is a silent defect: a wrong
 * width still draws a barcode, and the scanner at the lab reads a different job number or nothing
 * at all, which nobody discovers until a tray comes back wrong. It was extracted from a reference
 * encoder run once outside this repository, and the test checks three properties a transcription
 * error breaks: 95 patterns, each 6 elements summing to 11 modules, all distinct. The test then
 * compares whole encodings against vectors from that same reference run, and hand-computes one
 * checksum so the arithmetic is not only checked against itself.
 *
 * **Set B only, and no Set C switching.** Set C halves the width of a long run of digits and costs
 * a mode-switch state machine. Job numbers here are `BRANCHCODE-0001`: nine or ten characters with
 * letters in them, so Set C would never engage and the switching logic would be untested code
 * sitting on the path a lab scans.
 */

/**
 * Bar and space widths for Code Set B values 0 to 94, which are ASCII 32 to 126.
 *
 * Each string is six digits: bar, space, bar, space, bar, space, in modules. Six elements and
 * eleven modules is the Code 128 invariant, asserted in the test rather than trusted.
 */
const SET_B: readonly string[] = [
  "212222", // 0  space
  "222122", // 1  !
  "222221", // 2  "
  "121223", // 3  #
  "121322", // 4  $
  "131222", // 5  %
  "122213", // 6  &
  "122312", // 7  "
  "132212", // 8  (
  "221213", // 9  )
  "221312", // 10  *
  "231212", // 11  +
  "112232", // 12  ,
  "122132", // 13  -
  "122231", // 14  .
  "113222", // 15  /
  "123122", // 16  0
  "123221", // 17  1
  "223211", // 18  2
  "221132", // 19  3
  "221231", // 20  4
  "213212", // 21  5
  "223112", // 22  6
  "312131", // 23  7
  "311222", // 24  8
  "321122", // 25  9
  "321221", // 26  :
  "312212", // 27  ;
  "322112", // 28  <
  "322211", // 29  =
  "212123", // 30  >
  "212321", // 31  ?
  "232121", // 32  @
  "111323", // 33  A
  "131123", // 34  B
  "131321", // 35  C
  "112313", // 36  D
  "132113", // 37  E
  "132311", // 38  F
  "211313", // 39  G
  "231113", // 40  H
  "231311", // 41  I
  "112133", // 42  J
  "112331", // 43  K
  "132131", // 44  L
  "113123", // 45  M
  "113321", // 46  N
  "133121", // 47  O
  "313121", // 48  P
  "211331", // 49  Q
  "231131", // 50  R
  "213113", // 51  S
  "213311", // 52  T
  "213131", // 53  U
  "311123", // 54  V
  "311321", // 55  W
  "331121", // 56  X
  "312113", // 57  Y
  "312311", // 58  Z
  "332111", // 59  [
  "314111", // 60  \
  "221411", // 61  ]
  "431111", // 62  ^
  "111224", // 63  _
  "111422", // 64  `
  "121124", // 65  a
  "121421", // 66  b
  "141122", // 67  c
  "141221", // 68  d
  "112214", // 69  e
  "112412", // 70  f
  "122114", // 71  g
  "122411", // 72  h
  "142112", // 73  i
  "142211", // 74  j
  "241211", // 75  k
  "221114", // 76  l
  "413111", // 77  m
  "241112", // 78  n
  "134111", // 79  o
  "111242", // 80  p
  "121142", // 81  q
  "121241", // 82  r
  "114212", // 83  s
  "124112", // 84  t
  "124211", // 85  u
  "411212", // 86  v
  "421112", // 87  w
  "421211", // 88  x
  "212141", // 89  y
  "214121", // 90  z
  "412121", // 91  {
  "111143", // 92  |
  "111341", // 93  }
  "131141", // 94  ~
  /**
   * Values 95 to 102, which no Code Set B character maps to.
   *
   * **They are reachable as a check character, and leaving them out was a bug.** The checksum is
   * modulo 103, so it can land anywhere in 0 to 102, and the table stopping at 94 made the eight
   * highest check values unencodable. `~` is the smallest case that reaches one: its check value is
   * `(104 + 94) mod 103 = 95`, so the only barcode that could not be drawn was the one for the last
   * character in the set. The test that encodes all 95 characters is what found it.
   *
   * As characters these are the shifts and mode switches (`FNC3`, `FNC2`, `SHIFT`, `CODE C`,
   * `FNC4`, `FNC1`, and the three starts), and nothing here emits one: this encoder stays in Set B
   * from start to stop. They are here only because a check character is a value, not a character.
   */
  "114113", // 95
  "114311", // 96
  "411113", // 97
  "411311", // 98
  "113141", // 99
  "114131", // 100
  "311141", // 101
  "411131", // 102
]

/** `START B`, value 104. */
const START_B = '211214'
/** The stop pattern, value 106, which is thirteen modules rather than eleven. */
const STOP = '2331112'
/** Code Set B value 104, which seeds the weighted checksum. */
const START_B_VALUE = 104

export class Code128Error extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'Code128Error'
  }
}

/** The Code Set B value of one character, or undefined when it is outside ASCII 32 to 126. */
const valueOf = (character: string): number | undefined => {
  const point = character.codePointAt(0)
  if (point === undefined || point < 32 || point > 126) return undefined
  return point - 32
}

/**
 * The mod-103 check character's value. The Code 128 rule, which is the part worth stating:
 *
 * > sum = start value + (position * character value), positions counted from one, modulo 103.
 *
 * A wrong checksum is the failure that looks like success: the symbol draws, and every scanner
 * rejects it silently, so a lab types the number instead and nobody reports a bug.
 */
export function code128CheckValue(text: string): number {
  let sum = START_B_VALUE
  for (let i = 0; i < text.length; i += 1) {
    const character = text[i]
    const value = character === undefined ? undefined : valueOf(character)
    if (value === undefined) {
      throw new Code128Error(
        'CODE128_UNENCODABLE',
        `"${character ?? ''}" is outside the printable ASCII range Code Set B covers, so it cannot go on a barcode.`,
      )
    }
    sum += value * (i + 1)
  }
  return sum % 103
}

/**
 * The whole symbol as module widths: start, the characters, the check character, stop.
 *
 * Returned as numbers rather than as a drawing, so the renderer decides the module width and the
 * height. Appendix L asks for a ticket "designed to be photographed on one phone and read on
 * another", and how wide a module has to be for that is a stylesheet's problem.
 */
export function code128Widths(text: string): readonly number[] {
  if (text === '') {
    throw new Code128Error('CODE128_EMPTY', 'There is nothing to encode.')
  }
  const patterns: string[] = [START_B]
  for (const character of text) {
    const value = valueOf(character)
    if (value === undefined) {
      throw new Code128Error(
        'CODE128_UNENCODABLE',
        `"${character}" is outside the printable ASCII range Code Set B covers, so it cannot go on a barcode.`,
      )
    }
    const pattern = SET_B[value]
    if (pattern === undefined) {
      // Unreachable while the table has its 95 rows, which the test asserts. Named rather than
      // cast away, because a hole in the table must not quietly draw a shorter barcode.
      throw new Code128Error('CODE128_TABLE_INCOMPLETE', 'The Code 128 table is missing a row.')
    }
    patterns.push(pattern)
  }
  const check = SET_B[code128CheckValue(text)]
  if (check === undefined) {
    throw new Code128Error('CODE128_TABLE_INCOMPLETE', 'The Code 128 table is missing a row.')
  }
  patterns.push(check, STOP)

  return patterns.join('').split('').map(Number)
}

/**
 * The symbol as SVG path data, in modules, for a renderer to scale.
 *
 * Bars are the even-indexed runs, starting at the first. Drawn as one `<path>` rather than a run of
 * `<rect>` elements, because a photographed barcode depends on the bars having no seams between
 * them, and adjacent rects at a fractional scale leave hairlines a scanner reads as module edges.
 */
export function code128Path(text: string, height: number): string {
  const widths = code128Widths(text)
  const parts: string[] = []
  let x = 0
  for (let i = 0; i < widths.length; i += 1) {
    const width = widths[i] ?? 0
    if (i % 2 === 0) parts.push(`M${x} 0h${width}v${height}h-${width}z`)
    x += width
  }
  return parts.join('')
}

/** Total width in modules, so a renderer can set a viewBox without re-encoding. */
export function code128ModuleCount(text: string): number {
  return code128Widths(text).reduce((sum, width) => sum + width, 0)
}
